import React, { useRef, useState } from 'react';
import VerificationLandingPage from './pages/verificationLandingPage/VerificationLandingPage';
import VerificationSuccessPage from './pages/verificationSuccessPage/VerificationSuccessPage';
import { TvsState, useTvsState } from './utils/useTvsState';
import VeriffModalPage from './pages/veriffModalPage/VeriffModalPage';
import { useVeriffSession } from './utils/veriffSessionContext';
import { VerificationStatus } from './@types/VerificationStatus';
import FinapiStatusCheckPage from './pages/finapiStatusCheckPage/FinapiStatusCheckPage';
import VeriffStatusCheckPage from './pages/veriffStatusCheckPage/VeriffStatusCheckPage';
import { trackEvent } from './tracking/tracking';
import { useClientConfig } from './utils/clientConfigContext';
import { VerificationMethod } from './@types/VerificationMethod';
import { VerificationCompletionData } from './@types/VerificationCompletionData';

interface RootProps {
  onVerificationComplete?: (status: VerificationStatus, data: VerificationCompletionData) => void;
}

const Root: React.FC<RootProps> = ({ onVerificationComplete }) => {
  const [tvsState] = useTvsState();
  const { sessionUrl } = useVeriffSession();
  const { clientId } = useClientConfig();
  const isSchufaClient = clientId === 'boni_check';
  const prevStateRef = useRef<TvsState | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const sessionId = new URLSearchParams(window.location.search).get('tvs_session_id') || '';

  if (!tvsState) return null;

  const isNewState = tvsState !== prevStateRef.current;
  prevStateRef.current = tvsState;

  const handleError = (error: string) => {
    setErrorMessage(error);
  };

  let content = null;
  switch (tvsState) {
    case 'verification_success':
      content = <VerificationSuccessPage />;
      break;
    case 'verification_landing_page':
      content = <VerificationLandingPage />;
      break;
    case 'veriff_verification':
      content = sessionUrl ? <VeriffModalPage sessionUrl={sessionUrl} /> : null;
      break;
    case 'verification_check_finapi':
      content = <FinapiStatusCheckPage onVerificationComplete={onVerificationComplete} onError={handleError} />;
      break;
    case 'verification_check_veriff':
      content = <VeriffStatusCheckPage onVerificationComplete={onVerificationComplete} onError={handleError} />;
      break;
    case 'verification_failure_finapi_check':
      if (isNewState) {
        trackEvent(
          'is24.de.sicherheit.verifizierung.finapi',
          'verification_failed',
          'user',
          clientId,
          'finapi',
        ).catch();
      }

      content = (
        <VerificationLandingPage
          verificationErrorMessage={errorMessage || 'VerificationLandingPage.errorMessage'}
          defaultMethod={VerificationMethod.Finapi}
          isSchufaClient={isSchufaClient}
        />
      );
      break;
    case 'verification_failure_finapi_redirect':
      if (isNewState) {
        trackEvent(
          'is24.de.sicherheit.verifizierung.finapi',
          'verification_failed',
          'user',
          clientId,
          'finapi',
        ).catch();
        onVerificationComplete?.(VerificationStatus.FAILED, {
          sessionId: sessionId,
          verificationMethod: VerificationMethod.Finapi,
        });
      }
      content = (
        <VerificationLandingPage
          verificationErrorMessage={'VerificationLandingPage.errorMessage'}
          defaultMethod={VerificationMethod.Finapi}
          isSchufaClient={isSchufaClient}
        />
      );
      break;
    case 'verification_failure_veriff':
      if (isNewState) {
        trackEvent(
          'is24.de.sicherheit.verifizierung.veriff',
          'verification_failed',
          'user',
          clientId,
          'veriff',
        ).catch();
      }
      content = (
        <VerificationLandingPage
          verificationErrorMessage={errorMessage || 'VerificationLandingPage.errorMessage'}
          isSchufaClient={isSchufaClient}
        />
      );
      break;
    case 'verification_closed_veriff':
      content = <VerificationLandingPage verificationErrorMessage='VerificationLandingPage.errorMessage' />;
      break;
  }

  return <div className='modal'>{content}</div>;
};

export default Root;
